<template>
  <!-- Wider while a review is open: it is a table of payments, not a list of runs. -->
  <BaseSideModal :width="reviewing ? reviewWidth : '680px'" :show-footer="false" @close="close">
    <template #title>
      <span v-if="!reviewing">{{ $t('carriers.import.ImportHistory') }}</span>
    </template>

    <!-- In place of the list, as in All updates: a review reached from a list belongs behind a
         breadcrumb, not in a second window over the one it came from. -->
    <CarrierPaymentsReviewHost
      v-if="reviewing"
      :run="reviewing.run"
      :group="reviewing.group"
      :embedded="true"
      :back-label="$t('carriers.import.ImportHistory')"
      @close="closeReview"
    />

    <template v-else>
      <!-- See AllUpdatesDrawer: a second line in the title slot overflows the
           modal, because its content height assumes a one-line header. -->
      <p class="account-history__subtitle">
        {{ account.label || account.username }}
        <span v-if="account.portalName">· {{ account.portalName }}</span>
      </p>

      <ImportRunList
        ref="list"
        :runs="runs"
        :loading="loading"
        :highlight="lastReviewed"
        @reviewPayments="reviewing = $event"
      />
    </template>
  </BaseSideModal>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import { PAYMENTS_REVIEW_WIDTH } from '@/utils/carrierPayments';
import ImportRunList from './ImportRunList';
import CarrierPaymentsReviewHost from './CarrierPaymentsReviewHost';

/**
 * One portal account's import history.
 *
 * Its own window rather than a third section stacked under the credentials
 * form: settings and history answer different questions, and someone watching a
 * run finish should not have to scroll past a password field to see it.
 *
 * The runs come from the store, so the poll advances them while this is open.
 * The list it shows is the shared one, the same component All updates uses, so
 * the two cannot drift apart.
 */
export default {
  name: 'PortalAccountHistoryModal',
  components: { BaseSideModal, ImportRunList, CarrierPaymentsReviewHost },
  props: {
    account: { type: Object, required: true },
  },
  emits: ['close'],
  data() {
    return {
      reviewWidth: PAYMENTS_REVIEW_WIDTH,
      loading: true,
      /** The review open in place of the list, as `{ run, group }`. */
      reviewing: null,
      /** The run it was opened from, so the list says where you were. */
      lastReviewed: null,
    };
  },
  computed: {
    ...mapGetters(['carrierImportAccountRuns']),
    runs() {
      return this.carrierImportAccountRuns[this.account.scrapeAccountId] || [];
    },
  },
  created() {
    // Tell the store which account is on screen before fetching, so that if the
    // fetch lands on a live run the poll it starts already knows to keep this
    // list fresh too.
    this.$store.dispatch('carrierImportSetOpenAccount', this.account.scrapeAccountId);
    this.$store.dispatch('carrierImportFetchAccountRuns', this.account.scrapeAccountId)
      .finally(() => { this.loading = false; });
  },
  beforeDestroy() {
    this.$store.dispatch('carrierImportSetOpenAccount', null);
  },
  methods: {
    close() {
      this.$emit('close');
    },
    /** Back to the list, on the row that was being worked, with its counts refetched. */
    closeReview() {
      const { run } = this.reviewing || {};
      this.lastReviewed = run ? { scrapeJobId: run.scrapeJobId } : null;
      this.reviewing = null;
      if (run) this.$nextTick(() => this.$refs.list && this.$refs.list.refreshPayments(run));
    },
  },
};
</script>

<style lang="scss" scoped>
.account-history__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: var(--solid-dark-purple);
  margin-bottom: 12px;
}
</style>
